import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getAuthToken, setAuthToken } from './authApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const isAuthRequest = (url?: string): boolean => {
  return !!url && (url.includes('/auth/login') || url.includes('/auth/logout'));
};

// Event emitter for auth errors (to show toast from non-React context)
export const authErrorEvent = {
  listeners: new Set<(message: string) => void>(),
  emit(message: string) {
    this.listeners.forEach(cb => cb(message));
  },
  subscribe(cb: (message: string) => void) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  },
};

// Check if token is expired (JWT tokens are base64 encoded)
export const isTokenExpired = (token: string): boolean => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const { exp } = JSON.parse(jsonPayload);
    return exp ? Date.now() >= exp * 1000 : false;
  } catch {
    return true;
  }
};

// Perform logout cleanup
export const performLogout = (message: string = 'Your session has expired. Please log in again.') => {
  // Clear all auth data
  setAuthToken(null);
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth-storage');
  
  // Emit event for toast notification
  authErrorEvent.emit(message);
  
  // Redirect to login (with flag to show message)
  const currentPath = window.location.pathname;
  if (currentPath !== '/login') {
    sessionStorage.setItem('auth_error', message);
    window.location.href = '/login';
  }
};

// Request interceptor to add auth token and check expiration
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (isAuthRequest(config.url)) {
      delete config.headers.Authorization;
      return config;
    }

    const token = getAuthToken();
    
    if (token) {
      // Check if token is expired before making request
      if (isTokenExpired(token)) {
        performLogout('Your session has expired. Please log in again.');
        return Promise.reject(new Error('Token expired'));
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const errorData = error.response?.data as { message?: string } | undefined;
    const errorMessage = errorData?.message || '';
    
    // Handle authentication errors
    if (status === 401 || status === 403) {
      // Don't logout if it's a login request that failed (wrong credentials)
      const isLoginRequest = isAuthRequest(error.config?.url);
      
      if (!isLoginRequest) {
        // Check if it's a token expiration issue
        const isExpired = errorMessage.toLowerCase().includes('expired') || 
                         errorMessage.toLowerCase().includes('invalid') ||
                         isTokenExpired(getAuthToken() || '');
        
        const message = isExpired 
          ? 'Your session has expired. Please log in again.'
          : 'Authentication failed. Please log in again.';
        
        performLogout(message);
      }
    }
    
    return Promise.reject(error);
  }
);

export default client;

export interface ApiErrorResponse {
  status: number;
  message: string;
  timestamp?: string;
  errors?: Record<string, string>;
}

function asAxiosError(error: unknown): AxiosError {
  if (axios.isAxiosError(error)) {
    return error;
  }
  return new AxiosError(
    error instanceof Error ? error.message : 'An unexpected error occurred'
  );
}

export const handleApiError = (error: unknown): string => {
  const axiosError = asAxiosError(error);
  if (axiosError.response?.data && typeof axiosError.response.data === 'object') {
    const data = axiosError.response.data as ApiErrorResponse;
    
    // Handle validation errors with field-specific messages
    if (data.errors && Object.keys(data.errors).length > 0) {
      const fieldErrors = Object.entries(data.errors)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join(', ');
      return `Validation failed: ${fieldErrors}`;
    }
    
    // Return the specific error message from backend
    if (data.message) {
      return data.message;
    }
  }
  
  // Handle specific HTTP status codes
  const status = axiosError.response?.status;
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please log in.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return axiosError.response?.data && typeof axiosError.response.data === 'object'
        ? (axiosError.response.data as ApiErrorResponse).message || 'A conflict occurred. The item may already exist.'
        : 'A conflict occurred. The item may already exist.';
    case 422:
      return 'Unable to process the request. Please check your data.';
    case 500:
      return 'A server error occurred. Please try again later.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
  }
  
  return axiosError.message || 'An unexpected error occurred';
};

// Get detailed error info for better UI feedback
export const getApiErrorDetails = (error: unknown): ApiErrorResponse & { isValidationError: boolean; fieldErrors: Record<string, string> } => {
  const axiosError = asAxiosError(error);
  const defaultResponse = {
    status: axiosError.response?.status || 0,
    message: axiosError.message || 'An unexpected error occurred',
    isValidationError: false,
    fieldErrors: {} as Record<string, string>,
  };
  
  if (axiosError.response?.data && typeof axiosError.response.data === 'object') {
    const data = axiosError.response.data as ApiErrorResponse;
    
    return {
      ...defaultResponse,
      status: data.status || axiosError.response.status,
      message: data.message || defaultResponse.message,
      timestamp: data.timestamp,
      errors: data.errors,
      isValidationError: !!data.errors && Object.keys(data.errors).length > 0,
      fieldErrors: data.errors || {},
    };
  }
  
  return defaultResponse;
};
